import React from 'react';
import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { X, CheckSquare, Square, List } from 'lucide-react';
import BuyBoxIndicator from '../components/BuyBoxIndicator';
import {
  parseRawTags,
  parseNumericValue,
  getSalePriceValue,
  getMultiplierValue,
  formatCurrencyValue,
  renderCalculatedPriceBadge,
  formatMultiplierLabel,
  MPU_KEYS,
  MPUM_KEYS
} from '../utils/tableHelpers';

interface TableData {
  [key: string]: any;
}

interface TagColumnMeta {
  field: string;
  tagKey: string;
  tagDisplay: string;
}

interface CreateComparisonColumnsParams {
  comparisonData: any[];
  comparisonMode: boolean;
  data: TableData[];
  selectedProducts: Set<string>;
  toggleSelectAll: () => void;
  toggleProductSelection: (productId: string) => void;
  onRemoveFromComparison?: (productId: string) => void;
  openImageModal: (imageUrl: string, title: string) => void;
  enableSku: boolean;
  enableTags: boolean;
  isUnitPriceClient: boolean;
  tableName: string;
  expandedRows: Set<string>;
  isRowExpanded: (productId: string) => boolean;
  handleBuyboxToggle: (productId: string) => void;
  setLoadingVariants: React.Dispatch<React.SetStateAction<Set<string>>>;
  handleVariantsLoaded: (productId: string, variants: any[]) => void;
  skuValuesRef: React.MutableRefObject<{ [id_catalogo: string]: string }>;
  skusRef: React.MutableRefObject<{ [id_catalogo: string]: string }>;
  editingSkuRef: React.MutableRefObject<string | null>;
  handleSkuEditRef: React.MutableRefObject<(id_catalogo: string) => void>;
  handleSkuSaveRef: React.MutableRefObject<(id_catalogo: string) => void>;
  handleSkuCancelRef: React.MutableRefObject<(id_catalogo: string) => void>;
  handleSkuChangeRef: React.MutableRefObject<(id_catalogo: string, value: string) => void>;
  tagColumnsMeta: TagColumnMeta[];
  t: (key: string) => string;
  toTitleCase: (str: string) => string;
  getColumnDisplayName: (fieldName: string) => string;
  loadColumnPreferences: () => Promise<any>;
  setColumns: React.Dispatch<React.SetStateAction<ColumnDef<TableData>[]>>;
  setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setColumnVisibility: React.Dispatch<React.SetStateAction<VisibilityState>>;
}

export async function createComparisonColumns({
  comparisonData,
  comparisonMode,
  data,
  selectedProducts,
  toggleSelectAll,
  toggleProductSelection,
  onRemoveFromComparison,
  openImageModal,
  enableSku,
  enableTags,
  isUnitPriceClient,
  tableName,
  expandedRows,
  isRowExpanded,
  handleBuyboxToggle,
  setLoadingVariants,
  handleVariantsLoaded,
  skuValuesRef,
  skusRef,
  editingSkuRef,
  handleSkuEditRef,
  handleSkuSaveRef,
  handleSkuCancelRef,
  handleSkuChangeRef,
  tagColumnsMeta,
  t,
  toTitleCase,
  getColumnDisplayName,
  loadColumnPreferences,
  setColumns,
  setColumnOrder,
  setColumnVisibility,
}: CreateComparisonColumnsParams): Promise<void> {
    if (comparisonData.length === 0) return;

    // Detectar las columnas disponibles en los datos de comparación
    const sampleProduct = comparisonData[0];
    const availableFields = Object.keys(sampleProduct);

    // DEBUG: Log para ver qué campos están disponibles

    // Definir el orden deseado de las columnas (ahora incluyendo categoria y sitio)
    const desiredColumnOrder = [
      'actions',
      'imagen',
      'sku',
      'marca',
      'titulo',
      'buybox',
      'seller',
      'sitio',
      // 'tags', // Removido - no mostrar columna de tags
      'categoria',
      'pagina_orden',
      'precio',
      'precio_desc',
      'precio_venta',
      'cuotas',
      'precio_cuota'
    ];
    if (isUnitPriceClient) {
      const precioIndex = desiredColumnOrder.indexOf('precio');
      const insertPos = precioIndex >= 0 ? precioIndex + 1 : desiredColumnOrder.length;
      desiredColumnOrder.splice(insertPos, 0, 'precio_unidad', 'precio_unidad_medida');
    }

    const tableColumns: ColumnDef<TableData>[] = [];

    // Agregar columna de checkbox para selección múltiple (solo si no es modo comparación)
    if (!comparisonMode) {
      const currentPageIds = data.map(row => row.id_catalogo || row.ID_CATALOGO || row.id);
      const allSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedProducts.has(id));
      const someSelected = currentPageIds.some(id => selectedProducts.has(id));

      tableColumns.push({
        id: 'selection',
        header: () => (
          React.createElement('div', { className: 'flex justify-center' },
            React.createElement('button', {
              onClick: toggleSelectAll,
              className: 'p-1 hover:bg-gray-100 rounded transition-colors',
              title: allSelected ? t('alerts.deselect_all') : t('alerts.select_all'),
            },
              allSelected
                ? React.createElement(CheckSquare, { className: 'h-4 w-4 text-pink-500' })
                : someSelected
                  ? React.createElement(CheckSquare, { className: 'h-4 w-4 text-gray-400' })
                  : React.createElement(Square, { className: 'h-4 w-4 text-gray-400' })
            )
          )
        ),
        cell: ({ row }: { row: any }) => {
          const productId = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;
          const isSelected = selectedProducts.has(productId);
          return (
            React.createElement('div', { className: 'flex justify-center' },
              React.createElement('button', {
                onClick: () => toggleProductSelection(productId),
                className: 'p-1 hover:bg-gray-100 rounded transition-colors',
              },
                isSelected
                  ? React.createElement(CheckSquare, { className: 'h-4 w-4 text-pink-500' })
                  : React.createElement(Square, { className: 'h-4 w-4 text-gray-400 hover:text-gray-600' })
              )
            )
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
        size: 50,
      });
    }

    desiredColumnOrder.forEach(columnName => {
      if (columnName === 'precio_unidad' && isUnitPriceClient) {
        tableColumns.push({
          id: 'precio_unidad',
          header: t('columns.precio_unidad'),
          cell: ({ row }: { row: any }) => {
            const salePrice = getSalePriceValue(row.original);
            const multiplier = getMultiplierValue(row.original, MPU_KEYS);
            const precomputed = parseNumericValue(row.original.precio_unidad ?? row.original.precio_unidad_calc);
            const computed =
              precomputed !== null
                ? precomputed
                : salePrice !== null && multiplier !== null
                  ? salePrice * multiplier
                  : null;
            return renderCalculatedPriceBadge(computed, formatMultiplierLabel('MPU', multiplier));
          },
          enableSorting: true,
          enableColumnFilter: false,
          size: 160,
        });
        return;
      }
      if (columnName === 'precio_unidad_medida' && isUnitPriceClient) {
        tableColumns.push({
          id: 'precio_unidad_medida',
          header: t('columns.precio_unidad_medida'),
          cell: ({ row }: { row: any }) => {
            const salePrice = getSalePriceValue(row.original);
            const multiplier = getMultiplierValue(row.original, MPUM_KEYS);
            const precomputed = parseNumericValue(row.original.precio_unidad_medida ?? row.original.precio_unidad_medida_calc);
            const computed =
              precomputed !== null
                ? precomputed
                : salePrice !== null && multiplier !== null
                  ? salePrice * multiplier
                  : null;
            return renderCalculatedPriceBadge(computed, formatMultiplierLabel('MPUM', multiplier));
          },
          enableSorting: true,
          enableColumnFilter: false,
          size: 190,
        });
        return;
      }
      // console.log('🔧 Processing column:', columnName);
      if (columnName === 'actions') {
        // Columna de acciones para remover
        tableColumns.push({
          id: 'actions',
          header: '',
          cell: ({ row }: { row: any }) => {
            const product = row.original;
            // En modo comparación, siempre usar comparisonId si está disponible
            const productId = product.comparisonId || product.id_catalogo || product.ID_CATALOGO || product.id;

            return (
              React.createElement('div', { className: 'flex justify-center' },
                React.createElement('button', {
                  onClick: () => {
                    onRemoveFromComparison?.(productId);
                  },
                  className: 'p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors',
                  title: 'Remover de comparación',
                },
                  React.createElement(X, { className: 'h-4 w-4' })
                )
              )
            );
          },
          enableSorting: false,
          enableColumnFilter: false,
          size: 80,
        });
      } else if (columnName === 'imagen') {
        // Columna de imagen
        const imageField = availableFields.find(field =>
          field.toLowerCase().includes('imagen') ||
          field.toLowerCase().includes('image') ||
          field.toLowerCase().includes('img')
        );

        if (imageField) {
          tableColumns.push({
            id: 'imagen',
            header: t('columns.imagen'),
            accessorFn: (row: TableData) => row[imageField],
            cell: ({ getValue }: { getValue: () => any }) => {
              const imageUrl = getValue();
              if (imageUrl) {
                return (
                    React.createElement('div', { className: 'flex justify-center items-center' },
                        React.createElement('div', {
                            className: 'relative group w-16 h-14 flex justify-center items-center overflow-hidden rounded-lg border border-gray-200 cursor-pointer',
                            onClick: () => openImageModal(imageUrl, 'Producto'),
                        },
                            React.createElement('img', {
                                src: imageUrl,
                                alt: 'Producto',
                                className: 'max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-110',
                                onError: (e: any) => {
                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAyOEMyOS43OTA5IDI4IDI4IDI5Ljc5MDkgMjggMzJDMjggMzQuMjA5MSAyOS43OTA5IDM2IDMyIDM2QzM0LjIwOTEgMzYgMzYgMzQuMjA5MSAzNiAzMkMzNiAyOS43OTA5IDM0LjIwOTEgMjggMzIgMjhaIiBmaWxsPSIjOUNBM0FGIi8+CjxwYXRoIGQ9Ik0yNCA0MEw0MCA0MEw0MCAyNEwyNCAyNEwyNCA0MFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPC9zdmc+';
                                },
                            })
                        )
                    )
                );
              }
              return React.createElement('div', { className: 'w-16 h-14 flex justify-center items-center bg-gray-100 rounded-lg border border-gray-200' });
            },
            enableSorting: false,
            enableColumnFilter: false,
                         size: 100,
          });
        }
      } else if (columnName === 'sku') {
        // Columna SKU editable - solo si enableSku es true
        if (enableSku) {
          tableColumns.push({
            id: 'sku',
            header: t('columns.sku'),
            accessorFn: (row: TableData) => {
              const id_catalogo = row.id_catalogo || row.ID_CATALOGO || row.id;
              if (!id_catalogo) return '';
              const currentSkuValue = skuValuesRef.current?.[id_catalogo];
              const persistedSkuValue = skusRef.current?.[id_catalogo];
              return currentSkuValue ?? persistedSkuValue ?? '';
            },
            cell: ({ row }: { row: any }) => {
              const id_catalogo = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;
              if (!id_catalogo) {
                return React.createElement('span', { className: 'text-sm text-gray-400' }, '-');
              }
              const isEditing = editingSkuRef.current === id_catalogo;
              const currentSkuValue = skuValuesRef.current?.[id_catalogo];
              const persistedSkuValue = skusRef.current?.[id_catalogo];
              const value = currentSkuValue ?? persistedSkuValue ?? '';

              if (isEditing) {
                return (
                  React.createElement('div', { className: 'flex items-center gap-1' },
                    React.createElement('input', {
                      type: 'text',
                      value: value,
                      onChange: (e: any) => handleSkuChangeRef.current?.(id_catalogo, e.target.value),
                      onKeyDown: (e: any) => {
                        if (e.key === 'Enter') {
                          handleSkuSaveRef.current?.(id_catalogo);
                        } else if (e.key === 'Escape') {
                          handleSkuCancelRef.current?.(id_catalogo);
                        }
                      },
                      className: 'w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                      autoFocus: true,
                      placeholder: 'Ingrese SKU',
                    }),
                    React.createElement('button', {
                      onClick: () => handleSkuSaveRef.current?.(id_catalogo),
                      className: 'p-1 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors',
                      title: 'Guardar',
                    },
                      React.createElement('svg', { className: 'w-4 h-4', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
                        React.createElement('path', { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, d: 'M5 13l4 4L19 7' })
                      )
                    ),
                    React.createElement('button', {
                      onClick: () => handleSkuCancelRef.current?.(id_catalogo),
                      className: 'p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors',
                      title: 'Cancelar',
                    },
                      React.createElement(X, { className: 'w-4 h-4' })
                    )
                  )
                );
              }

              return (
                React.createElement('div', {
                  className: 'flex items-center justify-between group cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors',
                  onClick: () => handleSkuEditRef.current?.(id_catalogo),
                },
                  React.createElement('span', { className: `text-sm ${value ? 'text-gray-700' : 'text-gray-400'}` },
                    value || 'Click para editar'
                  ),
                  React.createElement('button', {
                    className: 'opacity-0 group-hover:opacity-100 p-1 text-blue-600 hover:text-blue-800 transition-opacity',
                    title: 'Editar SKU',
                  },
                    React.createElement('svg', { className: 'w-4 h-4', fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
                      React.createElement('path', { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: 2, d: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' })
                    )
                  )
                )
              );
            },
            enableSorting: true,
            enableColumnFilter: false,
            size: 180,
          });
        }
      } else if (columnName === 'precio_unidad' && isUnitPriceClient) {
        tableColumns.push({
          id: 'precio_unidad',
          header: t('columns.precio_unidad'),
          accessorFn: (row: TableData) => {
            const precomputed = row.precio_unidad ?? row.precio_unidad_calc;
            if (precomputed !== undefined && precomputed !== null) {
              return precomputed;
            }
            const salePrice = getSalePriceValue(row);
            const multiplier = getMultiplierValue(row, MPU_KEYS);
            return salePrice !== null && multiplier !== null ? salePrice * multiplier : null;
          },
          cell: ({ row }: { row: any }) => {
            const salePrice = getSalePriceValue(row.original);
            const multiplier = getMultiplierValue(row.original, MPU_KEYS);
            const precomputed = parseNumericValue(row.original.precio_unidad ?? row.original.precio_unidad_calc);
            const computed =
              precomputed !== null
                ? precomputed
                : salePrice !== null && multiplier !== null
                  ? salePrice * multiplier
                  : null;
            return renderCalculatedPriceBadge(computed, formatMultiplierLabel('MPU', multiplier));
          },
          enableSorting: true,
          enableColumnFilter: false,
          size: 160,
        });
      } else if (columnName === 'precio_unidad_medida' && isUnitPriceClient) {
        tableColumns.push({
          id: 'precio_unidad_medida',
          header: t('columns.precio_unidad_medida'),
          accessorFn: (row: TableData) => {
            const precomputed = row.precio_unidad_medida ?? row.precio_unidad_medida_calc;
            if (precomputed !== undefined && precomputed !== null) {
              return precomputed;
            }
            const salePrice = getSalePriceValue(row);
            const multiplier = getMultiplierValue(row, MPUM_KEYS);
            return salePrice !== null && multiplier !== null ? salePrice * multiplier : null;
          },
          cell: ({ row }: { row: any }) => {
            const salePrice = getSalePriceValue(row.original);
            const multiplier = getMultiplierValue(row.original, MPUM_KEYS);
            const precomputed = parseNumericValue(row.original.precio_unidad_medida ?? row.original.precio_unidad_medida_calc);
            const computed =
              precomputed !== null
                ? precomputed
                : salePrice !== null && multiplier !== null
                  ? salePrice * multiplier
                  : null;
            return renderCalculatedPriceBadge(computed, formatMultiplierLabel('MPUM', multiplier));
          },
          enableSorting: true,
          enableColumnFilter: false,
          size: 190,
        });
      } else if (columnName === 'tags') {
        // Solo agregar columna de tags si enableTags es true
        if (enableTags) {
          const tagCandidates = [
            'tags',
            'tag',
            'tag_status',
            'estado_in_out',
            'estadoinout',
            'estado',
            'movimiento',
            'movimientos',
            'in_out',
            'inout'
          ];

          const tagsField = availableFields.find(field => {
            const lower = field.toLowerCase();
            return tagCandidates.includes(lower) || lower.includes('tag') || lower.includes('in_out') || lower.includes('estado');
          });

          if (tagsField) {
            tableColumns.push({
            id: 'tags',
            header: t('columns.tags'),
            accessorFn: (row: TableData) => row[tagsField],
            cell: ({ row }: { row: any }) => {
              const rawValue = row.original?.[tagsField];
              const parsedTags = parseRawTags(rawValue);

              if (parsedTags.length === 0) {
                return React.createElement('span', { className: 'text-gray-400' }, '-');
              }

              return (
                React.createElement('div', { className: 'flex flex-wrap gap-1 max-w-xl' },
                  parsedTags.map((tag, index) => (
                    React.createElement('span', {
                      key: `${tag.key ?? 'value'}-${tag.value}-${index}`,
                      className: 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200',
                    },
                      tag.key
                        ? React.createElement(React.Fragment, null,
                            React.createElement('span', { className: 'font-semibold uppercase tracking-wide text-purple-600' }, tag.key),
                            React.createElement('span', { className: 'mx-1 text-purple-300' }, ':'),
                            React.createElement('span', { className: 'text-purple-800' }, tag.value)
                          )
                        : React.createElement('span', { className: 'text-purple-700' }, tag.value)
                    )
                  ))
                )
              );
            },
            enableSorting: false,
            enableColumnFilter: false,
            size: 320,
            });
          }
        }
      } else if (columnName === 'buybox') {
        // Columna de BuyBox - solo mostrar si hay campos buybox en los datos
        const buyboxIdField = availableFields.find(field =>
          field.toLowerCase() === 'buybox_id' || field === 'buybox_id'
        );
        const buyboxWinnerField = availableFields.find(field =>
          field.toLowerCase() === 'buybox_winner' || field === 'buybox_winner'
        );

        if (buyboxIdField || buyboxWinnerField) {
          // Always add BuyBox column if the fields exist
          tableColumns.push({
            id: 'buybox',
            header: t('columns.buybox'),
            cell: ({ row }: { row: any }) => {
              const rawBuyboxId = row.original.buybox_id;
              const rawBuyboxWinner = row.original.buybox_winner;
              const rawSeller = row.original.Seller || row.original.seller || '';

              // Debug log para los primeros productos
              if ((window as any).buyboxDebugCount < 10) {
                if (!(window as any).buyboxDebugCount) (window as any).buyboxDebugCount = 0;
                (window as any).buyboxDebugCount++;
              }

              // Verificar si buybox_id es realmente NULL o tiene un valor válido
              const isValidBuyboxId = rawBuyboxId !== null &&
                                    rawBuyboxId !== undefined &&
                                    rawBuyboxId !== '' &&
                                    rawBuyboxId !== '(NULL)' &&
                                    rawBuyboxId !== 'NULL' &&
                                    rawBuyboxId !== '0' &&
                                    rawBuyboxId !== 0;

              const buyboxId = isValidBuyboxId ? rawBuyboxId : null;
              // Manejar diferentes formatos de buybox_winner incluyendo NULL
              const isBuyboxWinner = rawBuyboxWinner === 1 ||
                                   rawBuyboxWinner === '1' ||
                                   rawBuyboxWinner === true ||
                                   rawBuyboxWinner === 'true' ||
                                   parseInt(rawBuyboxWinner) === 1;

              // Log para debug - solo para los primeros 5 productos con buybox_id
              if (buyboxId && (window as any).buyboxLogCount < 5) {
                if (!(window as any).buyboxLogCount) (window as any).buyboxLogCount = 0;
                (window as any).buyboxLogCount++;
              }

              // Validación adicional: solo considerar válidos los IDs numéricos
              if (buyboxId && !/^\d+$/.test(String(buyboxId))) {
                return null;
              }

              // Solo mostrar si tiene buybox_id Y es winner
              if (!buyboxId || !isBuyboxWinner) {
                return null;
              }

              const productId = row.original.id_catalogo || row.original.ID_CATALOGO || row.original.id;

              // En modo comparación, usar el originalTableName del producto
              const effectiveTableName = comparisonMode && row.original.originalTableName
                ? row.original.originalTableName
                : tableName;

              return (
                React.createElement(BuyBoxIndicator, {
                  isBuyboxWinner: isBuyboxWinner,
                  buyboxId: buyboxId,
                  seller: rawSeller,
                  isExpanded: isRowExpanded(productId),
                  onToggle: () => {
                    handleBuyboxToggle(productId);
                    // Always set loading when toggling
                    if (!expandedRows.has(productId)) {
                      setLoadingVariants(prev => new Set(prev).add(productId));
                    }
                  },
                  showExpandButton: true,
                  tableName: effectiveTableName,
                  onVariantsLoaded: (variants: any[]) => handleVariantsLoaded(productId, variants),
                })
              );
            },
            enableSorting: false,
            enableColumnFilter: false,
            size: 100,
          });
        }
      } else {
                 // Para otras columnas, buscar el campo correspondiente
         const fieldMapping: { [key: string]: string[] } = {
           'marca': ['Marca', 'marca', 'brand', 'MARCA'],
           'titulo': ['titulo', 'title', 'nombre', 'name', 'TITULO'],
           'sitio': ['sitio', 'Sitio', 'site', 'website', 'tienda', 'store', 'SITIO', 'SITE'],
           'categoria': ['Cat', 'cat', 'CAT', 'categoria', 'category', 'tipo', 'type', 'CATEGORIA', 'CATEGORY'],
           'precio': ['precio', 'price', 'original_price', 'PRECIO'],
           'precio_desc': ['precio_desc', 'discounted_price', 'precio_descuento', 'PRECIO_DESC'],
           'precio_venta': ['precio_venta', 'sale_price', 'PRECIO_VENTA'],
           'cuotas': ['cuotas', 'installments', 'installements', 'CUOTAS'],
           'precio_cuota': ['precio_cuota', 'installment_value', 'installements_value', 'PRECIO_CUOTA'],
           'pagina_orden': ['pagina', 'page', 'PAGINA', 'PAGE', 'orden', 'order', 'ORDEN', 'ORDER', 'pagina_orden', 'page_order'],
           'seller': ['Seller', 'seller', 'SELLER', 'vendedor', 'vendor', 'VENDEDOR']
         };

                 if (columnName === 'pagina_orden') {
           // Las posiciones ahora solo vienen del campo apariciones
           const aparicionesField = availableFields.find(field =>
             field.toLowerCase() === 'apariciones'
           );

           if (aparicionesField) {
             tableColumns.push({
               id: 'pagina_orden',
               header: t('columns.pagina_orden'),
               accessorFn: (row: TableData) => {
                 // Parsear apariciones para obtener pagina y orden
                 let apariciones: any[] = [];
                 if (row.apariciones) {
                   try {
                     apariciones = typeof row.apariciones === 'string'
                       ? JSON.parse(row.apariciones)
                       : row.apariciones;
                     apariciones = Array.isArray(apariciones) ? apariciones : [];
                   } catch (e) {
                     apariciones = [];
                   }
                 }

                 if (apariciones.length > 0) {
                   // Usar la menor posición para ordenamiento
                   const sortedApariciones = apariciones.sort((a: any, b: any) => {
                     const pageA = parseInt(a.page) || 999;
                     const pageB = parseInt(b.page) || 999;
                     if (pageA !== pageB) return pageA - pageB;
                     const orderA = parseInt(a.orderInPage) || 999;
                     const orderB = parseInt(b.orderInPage) || 999;
                     return orderA - orderB;
                   });
                   return {
                     pagina: sortedApariciones[0].page || 0,
                     orden: sortedApariciones[0].orderInPage || 0
                   };
                 }

                 // Si no hay apariciones, retornar valores vacíos
                 return { pagina: 0, orden: 0 };
               },
                                cell: ({ row, getValue }: { row: any, getValue: () => any }) => {
                   const product = row.original;

                   // Verificar si hay apariciones múltiples
                   let apariciones: any[] = [];
                   if (product.apariciones) {
                     try {
                       apariciones = typeof product.apariciones === 'string'
                         ? JSON.parse(product.apariciones)
                         : product.apariciones;
                       apariciones = Array.isArray(apariciones) ? apariciones : [];
                     } catch (e) {
                       apariciones = [];
                     }
                   }

                   const hasMultipleApariciones = apariciones.length > 1;

                   if (hasMultipleApariciones) {
                     return (
                       React.createElement('div', { className: 'text-center relative group' },
                         React.createElement('div', { className: 'flex items-center justify-center space-x-1 cursor-help' },
                           React.createElement(List, { className: 'h-4 w-4 text-gray-500' }),
                           React.createElement('span', { className: 'text-xs text-gray-600' }, apariciones.length)
                         ),
                         /* Tooltip con todas las apariciones */
                         React.createElement('div', { className: 'absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 z-50' },
                           React.createElement('div', { className: 'bg-gray-800 text-white p-2 rounded-lg shadow-lg min-w-[200px] max-w-[300px]' },
                             React.createElement('div', { className: 'text-xs font-semibold mb-1 border-b border-gray-600 pb-1' },
                               `Múltiples apariciones (${apariciones.length})`
                             ),
                             React.createElement('div', { className: 'space-y-1 max-h-48 overflow-y-auto' },
                               apariciones.map((ap: any, idx: number) => (
                                 React.createElement('div', { key: idx, className: 'text-xs text-left' },
                                   React.createElement('span', { className: 'font-medium' },
                                     `${ap.category_toma || 'Sin categoría'}:`
                                   ),
                                   ' ',
                                   React.createElement('span', { className: 'text-gray-300' },
                                     `Pág. ${String(ap.page || 0).padStart(2, '0')}, Pos. ${String(ap.orderInPage || 0).padStart(2, '0')}`
                                   )
                                 )
                               ))
                             )
                           )
                         )
                       )
                     );
                   } else {
                     // Mostrar posición normal o desde la primera aparición
                     let pagina, orden;
                     if (apariciones.length === 1) {
                       pagina = apariciones[0].page;
                       orden = apariciones[0].orderInPage;
                     } else {
                       const { pagina: p, orden: o } = getValue();
                       pagina = p;
                       orden = o;
                     }

                     const paginaFormatted = pagina ? String(pagina).padStart(3, '0') : '000';
                     const ordenFormatted = orden ? String(orden).padStart(3, '0') : '000';
                     return (
                       React.createElement('div', { className: 'text-center' },
                         React.createElement('span', { className: 'text-xs text-gray-600 font-mono' },
                           `${paginaFormatted}-${ordenFormatted}`
                         )
                       )
                     );
                   }
                 },
               enableSorting: false,
               enableColumnFilter: false,
               size: 120,
             });
           }
                    } else {
           const possibleFields = fieldMapping[columnName] || [columnName];
           const matchedField = availableFields.find(field =>
             possibleFields.some(possible => {
               // Para todos los campos, hacer matching exacto (case-insensitive)
               return field === possible || field.toLowerCase() === possible.toLowerCase();
             })
           );

           // Debug para campos problemáticos
           if (columnName === 'sitio' || columnName === 'seller') {
           }

           // DEBUG: Log específico para categoría
           if (columnName === 'categoria') {
           }

           // Para seller, verificar si el campo existe
           let shouldAddColumn = true;
           if (columnName === 'seller' && matchedField) {
             // Always add seller column if the field exists, even if some products don't have data
             shouldAddColumn = true;
           }

           if (matchedField && shouldAddColumn) {
             tableColumns.push({
               id: columnName,
               header: getColumnDisplayName(columnName),
               accessorFn: (row: TableData) => row[matchedField],
               cell: ({ getValue }: { getValue: () => any }) => {
                 const value = getValue();

                 // Formateo específico por tipo de columna
                 if (columnName === 'precio' || columnName === 'precio_desc' || columnName === 'precio_venta' || columnName === 'precio_cuota') {
                   const numericValue = parseFloat(value);
                   if (!isNaN(numericValue) && numericValue > 0) {
                     return (
                       React.createElement('div', { className: 'text-right' },
                        React.createElement('span', { className: 'font-bold text-gray-700 bg-gray-200 px-2 py-1 rounded-md whitespace-nowrap inline-block' },
                          formatCurrencyValue(numericValue)
                        )
                       )
                     );
                   }
                   return React.createElement('span', { className: 'text-gray-400' }, '-');
                 }

                                if (columnName === 'titulo') {
                 return (
                   React.createElement('div', { className: 'w-full' },
                     React.createElement('div', { className: 'text-sm font-medium text-gray-900 leading-tight truncate' },
                       toTitleCase(String(value || ''))
                     )
                   )
                 );
               }

                 if (columnName === 'seller') {
                   return (
                     React.createElement('div', { className: 'text-center' },
                       React.createElement('span', { className: 'inline-block px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full' },
                         String(value || '-')
                       )
                     )
                   );
                 }

                 if (columnName === 'marca') {
                   return (
                     React.createElement('div', { className: 'text-center' },
                       React.createElement('span', { className: 'inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full' },
                         toTitleCase(String(value || ''))
                       )
                     )
                   );
                 }

                 if (columnName === 'sitio') {
                   return (
                     React.createElement('div', { className: 'text-center' },
                       React.createElement('span', { className: 'inline-block px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full' },
                         toTitleCase(String(value || ''))
                       )
                     )
                   );
                 }

                 if (columnName === 'categoria') {
                   // Debug logging for category values
                   if (typeof value === 'object' && value !== null) {
                   }

                   // Handle both string and object values
                   let displayValue = '';
                   if (typeof value === 'string') {
                     displayValue = value;
                   } else if (typeof value === 'object' && value !== null) {
                     // If it's an object, try to extract a meaningful value
                     displayValue = value.name || value.value || JSON.stringify(value);
                   } else {
                     displayValue = String(value || '');
                   }

                   return (
                     React.createElement('div', { className: 'text-center' },
                       React.createElement('span', { className: 'inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full' },
                         toTitleCase(displayValue)
                       )
                     )
                   );
                 }

                 if (columnName === 'cuotas') {
                 const numericValue = parseInt(value);
                 if (!isNaN(numericValue) && numericValue > 0) {
                   return (
                     React.createElement('div', { className: 'text-center' },
                       React.createElement('span', { className: 'bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold' },
                         `${numericValue}x`
                       )
                     )
                   );
                 }
                 return React.createElement('span', { className: 'text-gray-400' }, '-');
               }

               return String(value || '');
               },
               enableSorting: columnName !== 'imagen' && columnName !== 'actions',
               enableColumnFilter: false,
                            size: columnName === 'titulo' ? 380 :
                   columnName === 'actions' ? 80 :
                   columnName === 'imagen' ? 100 :
                   columnName === 'marca' ? 150 :
                   columnName === 'sitio' ? 120 :
                   columnName === 'categoria' ? 140 :
                   columnName === 'precio' ? 120 :
                   columnName === 'precio_desc' ? 120 :
                   columnName === 'precio_venta' ? 120 :
                   columnName === 'cuotas' ? 80 :
                   columnName === 'precio_cuota' ? 120 :
                   columnName === 'pagina_orden' ? 100 :
                   columnName === 'seller' ? 120 : 120,
             });
           }
         }
      }
    });

    // Deduplicar columnas por ID para evitar React duplicate key warnings
    const seenIds = new Set<string>();
    const deduplicatedColumns: ColumnDef<TableData>[] = [];
    for (const col of tableColumns) {
      const colId = (col as any).id || '';
      if (!seenIds.has(colId)) {
        seenIds.add(colId);
        deduplicatedColumns.push(col);
      }
    }
    const finalColumns = deduplicatedColumns;

    // Cargar preferencias guardadas para modo comparación
    const savedPreferences = await loadColumnPreferences();

    // Aplicar orden guardado si existe
    if (savedPreferences && savedPreferences.order && savedPreferences.order.length > 0) {
      // Deduplicar savedOrder para evitar columnas duplicadas de preferencias antiguas
      const seenSavedIds = new Set<string>();
      const savedOrder = savedPreferences.order.filter((id: string) => {
        if (seenSavedIds.has(id)) return false;
        seenSavedIds.add(id);
        return true;
      });

      // Reordenar columnas según preferencias guardadas
      const reorderedColumns = savedOrder
        .map((columnId: string) => finalColumns.find(col => col.id === columnId))
        .filter((col: any): col is ColumnDef<TableData> => col !== undefined) // Filtrar columnas que ya no existen
        .concat(finalColumns.filter(col => !savedOrder.includes(col.id!))); // Agregar nuevas columnas al final

      setColumns(reorderedColumns);
      const mergedOrder = [
        ...savedOrder,
        ...finalColumns
          .map(col => col.id!)
          .filter(id => !!id && !savedOrder.includes(id))
      ];
      // Asegurar que 'selection' y 'alert' estén al inicio si no es modo comparación
      let finalMergedOrder = mergedOrder.filter((c: string) => c !== 'selection' && c !== 'alert');
      if (!comparisonMode) {
        if (finalColumns.some(c => c.id === 'alert')) {
          finalMergedOrder = ['alert', ...finalMergedOrder];
        }
        if (finalColumns.some(c => c.id === 'selection')) {
          finalMergedOrder = ['selection', ...finalMergedOrder];
        }
      }
      setColumnOrder(finalMergedOrder);

      // Aplicar visibilidad guardada si existe
        if (savedPreferences.visibility) {
          // Asegurar que las columnas nuevas estén visibles
          const updatedVisibility = { ...savedPreferences.visibility };

          // Forzar visibilidad para columnas críticas
          if (!comparisonMode && finalColumns.some(col => col.id === 'selection')) {
            updatedVisibility.selection = true;
          }
          if (!comparisonMode && finalColumns.some(col => col.id === 'alert')) {
            updatedVisibility.alert = true;
          }
          if (finalColumns.some(col => col.id === 'seller')) {
            updatedVisibility.seller = true;
          }
          if (finalColumns.some(col => col.id === 'buybox')) {
            updatedVisibility.buybox = true;
          }
          if (finalColumns.some(col => col.id === 'sitio')) {
            updatedVisibility.sitio = true;
          }
          // Forzar visibilidad de columnas de tags (siempre visibles)
          tagColumnsMeta.forEach((column: TagColumnMeta) => {
            if (column.field) {
              updatedVisibility[column.field] = true;
            }
          });

          setColumnVisibility(updatedVisibility);
        }
    } else {
      // Sin preferencias guardadas, usar orden por defecto
      setColumns(finalColumns);
      // Agregar 'selection' y 'alert' al inicio si no es modo comparación
      let baseOrder = desiredColumnOrder.filter(col =>
        finalColumns.some(tcol => tcol.id === col)
      );
      baseOrder = baseOrder.filter(c => c !== 'selection' && c !== 'alert');
      if (!comparisonMode) {
        if (finalColumns.some(c => c.id === 'alert')) {
          baseOrder = ['alert', ...baseOrder];
        }
        if (finalColumns.some(c => c.id === 'selection')) {
          baseOrder = ['selection', ...baseOrder];
        }
      }
      setColumnOrder(baseOrder);

      // Resetear visibilidad para mostrar todas las columnas
      const allVisible: VisibilityState = {};
      tableColumns.forEach(col => {
        if (col.id) {
          allVisible[col.id] = true;
        }
      });
      setColumnVisibility(allVisible);
    }
}
